import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { changePassword } from "src/ui/containers/account/api";
import ApmLink from "src/ui/shared/ApmLink";
import { PrimaryButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { makeStyles } from "tss-react/mui";

import { Theme, Typography } from "@mui/material";
import PasswordField from "@shared/form/PasswordField";
import { SectionFormStack, SectionInputSlot } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

const useStyles = makeStyles()((theme: Theme) => ({
    descriptionText: {
        marginBottom: theme.spacing(2),
    },
}));

type FormValues = {
    currentPassword: string;
    newPasswordValue: string;
    newPasswordRepeat: string;
};

type PasswordChangeFormProps = {
    onCancel: () => void;
};

export const PasswordChangeForm = ({ onCancel }: PasswordChangeFormProps) => {
    const navigate = useNavigate();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const onSubmit = async (values: FormValues) => {
        try {
            await changePassword(values);
            showSuccessToast(t("passwordChangePage.toasts.success"));
            void navigate("/login");
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    };

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.currentPassword) {
            errors.currentPassword = t("passwordChangePage.validation.currentPasswordRequired");
        }

        if (!values.newPasswordValue) {
            errors.newPasswordValue = t("passwordChangePage.validation.newPasswordRequired");
        }

        if (!values.newPasswordRepeat) {
            errors.newPasswordRepeat = t("passwordChangePage.validation.passwordRepeatRequired");
        } else if (values.newPasswordValue !== values.newPasswordRepeat) {
            errors.newPasswordRepeat = t("passwordChangePage.validation.passwordsDoNotMatch");
        }

        return errors;
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            render={({ handleSubmit, submitting }) => (
                <form onSubmit={handleSubmit}>
                    <SectionPanel
                        title={t("passwordChangePage.title")}
                        actions={
                            <>
                                <SecondaryButton type="button" onClick={onCancel} disabled={submitting}>
                                    {t("common:button.cancel")}
                                </SecondaryButton>
                                <PrimaryButton type="submit" loading={submitting} disabled={submitting}>
                                    {t("common:button.submit")}
                                </PrimaryButton>
                            </>
                        }
                    >
                        <Typography variant="body2" color="text.secondary" className={classes.descriptionText}>
                            {t("passwordChangePage.description")}{" "}
                            <ApmLink to="/account/recover">{t("passwordChangePage.resetLink")}</ApmLink>.
                        </Typography>
                        <SectionFormStack>
                            <SectionInputSlot>
                                <PasswordField
                                    name="currentPassword"
                                    label={t("passwordChangePage.currentPasswordLabel")}
                                    fullWidth
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <PasswordField
                                    name="newPasswordValue"
                                    label={t("passwordChangePage.newPasswordLabel")}
                                    fullWidth
                                />
                            </SectionInputSlot>
                            <SectionInputSlot>
                                <PasswordField
                                    name="newPasswordRepeat"
                                    label={t("passwordChangePage.repeatPasswordLabel")}
                                    fullWidth
                                />
                            </SectionInputSlot>
                        </SectionFormStack>
                    </SectionPanel>
                </form>
            )}
        />
    );
};
